import { Link } from 'gatsby';
import { Search as SearchIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Box, Button, Flex, Text } from 'theme-ui';
import SearchDialog from './SearchDialog';
import ThemeToggle from './ThemeToggle';

interface HeaderProps {
  onMenuClick?: () => void;
  onNotificationClick?: () => void;
  onSearchClick?: () => void;
  children: React.ReactNode;
}

const HEADER_ACTION_SIZE = 36;

const Header: React.FC<HeaderProps> = ({ children }) => {
  const [showSearch, setShowSearch] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);

  useEffect(() => {
    if (!showSearch) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowSearch(false);
        setHasFocus(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSearch]);

  return (
    <Box
      as="header"
      sx={{
        width: '100%',
        backgroundColor: 'background',
        padding: '8px 16px',
        position: 'relative',
      }}
    >
      <Flex
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: HEADER_ACTION_SIZE,
        }}
      >
        <Flex
          sx={{
            alignItems: 'center',
            gap: 3,
            minHeight: HEADER_ACTION_SIZE,
          }}
        >
          <Link
            to="/"
            style={{
              textDecoration: 'none',
              color: 'inherit',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Text sx={{ fontSize: 3, fontWeight: 'bold', lineHeight: 1, m: 0 }}>
              {children}
            </Text>
          </Link>
        </Flex>

        <Flex sx={{ gap: 2, alignItems: 'center', minHeight: HEADER_ACTION_SIZE }}>
          <Flex
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              width: HEADER_ACTION_SIZE,
              height: HEADER_ACTION_SIZE,
            }}
          >
            <ThemeToggle />
          </Flex>
          <Button
            variant="icon"
            onClick={() => {
              setShowSearch(true);
              setHasFocus(true);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: HEADER_ACTION_SIZE,
              height: HEADER_ACTION_SIZE,
              p: 0,
            }}
            aria-label="Buscar"
          >
            <SearchIcon size={20} />
          </Button>
        </Flex>
      </Flex>
      {showSearch && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bg: 'overlay',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setShowSearch(false)}
        >
          <Box
            sx={{
              bg: 'background',
              p: [3, 4],
              borderRadius: 4,
              minWidth: ['90vw', '600px'],
              maxWidth: '95vw',
              boxShadow: 'shadow',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="icon"
              onClick={() => setShowSearch(false)}
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                zIndex: 10,
                fontSize: 4,
                color: 'text',
                bg: 'transparent',
                '&:hover': { color: 'primary', bg: 'muted' },
              }}
              aria-label="Cerrar búsqueda"
            >
              Cancel
            </Button>
            <SearchDialog
              hasFocus={hasFocus}
              onClose={() => setShowSearch(false)}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Header;
